import { useState } from 'react'
import { Button } from 'components/common'
import { Table, TableCell, TableRow } from 'components/common/minimalTable'
import Modal from 'components/modal'
import { PlusIcon } from 'components/icons'
import { useAuth } from 'hooks'
import { checkPageAccess, formatDate } from 'utils/helpers'
import { PAGE_PURCHASE_TIMER } from 'utils/constants'
import { deletePurchaseTimer } from 'utils/api/purchaseTimerApi'
import toast from 'react-hot-toast'
import PurchaseTimerFormModal, { PurchaseTimer } from './purchaseTimerFormModal'

type Props = {
  barcode: string
  purchaseTimers?: PurchaseTimer[]
  onRefetch: () => void
}

const PurchaseTimerSection = ({
  barcode,
  purchaseTimers = [],
  onRefetch,
}: Props) => {
  const { user } = useAuth()
  // Write actions are gated on editor access to the Purchase Timer page,
  // not the Master Data page this section is embedded in.
  const { isEditor } = checkPageAccess(PAGE_PURCHASE_TIMER.id, user?.pageAccess)

  const [formOpen, setFormOpen] = useState(false)
  const [selectedTimer, setSelectedTimer] = useState<PurchaseTimer | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PurchaseTimer | null>(null)
  const [deleting, setDeleting] = useState(false)

  const openAdd = () => {
    setSelectedTimer(null)
    setFormOpen(true)
  }

  const openEdit = (timer: PurchaseTimer) => {
    setSelectedTimer(timer)
    setFormOpen(true)
  }

  const closeForm = () => {
    setFormOpen(false)
    setSelectedTimer(null)
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    const toastId = toast.loading('Deleting Purchase Timer...')
    try {
      await deletePurchaseTimer(deleteTarget._id)
      toast.success('Purchase Timer deleted successfully!', { id: toastId })
      setDeleteTarget(null)
      onRefetch()
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.msg
      toast.error(msg || 'Failed to delete Purchase Timer.', { id: toastId })
    } finally {
      setDeleting(false)
    }
  }

  const headers = [
    'Store',
    'Available',
    'Start Time (GST)',
    'End Time (GST)',
    'Updated At',
    ...(isEditor ? ['Actions'] : []),
  ]

  return (
    <>
      <div className="mt-3 flex items-center justify-between">
        <h3 className="title">Purchase Timer</h3>
        {isEditor && (
          <Button
            buttonText="Add Timer"
            size="small"
            icon={<PlusIcon />}
            type="button"
            onClick={openAdd}
          />
        )}
      </div>

      {purchaseTimers.length > 0 ? (
        <Table headers={headers}>
          {purchaseTimers.map((timer) => (
            <TableRow key={timer._id}>
              <TableCell>{timer.store || '-'}</TableCell>
              <TableCell>
                {timer.availableToPurchaseTimer ? 'Yes' : 'No'}
              </TableCell>
              <TableCell>{timer.purchaseStartTime || '-'}</TableCell>
              <TableCell>{timer.purchaseEndTime || '-'}</TableCell>
              <TableCell>
                {timer.updatedAt ? formatDate(timer.updatedAt) : '-'}
              </TableCell>
              {isEditor && (
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      buttonText="Edit"
                      size="small"
                      variant="outline"
                      type="button"
                      onClick={() => openEdit(timer)}
                    />
                    <Button
                      buttonText="Delete"
                      size="small"
                      variant="outline"
                      type="button"
                      onClick={() => setDeleteTarget(timer)}
                    />
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </Table>
      ) : (
        <p className="rounded-lg border bg-white px-5 py-4 text-sm text-gray-500">
          No purchase timer configured for this product.
        </p>
      )}

      {formOpen && (
        <PurchaseTimerFormModal
          isOpen
          closeModal={closeForm}
          barcode={barcode}
          timer={selectedTimer}
          onSuccess={onRefetch}
        />
      )}

      {deleteTarget && (
        <Modal
          width={500}
          title="Delete Purchase Timer"
          isOpen
          closeModal={() => setDeleteTarget(null)}
        >
          <>
            <p className="text-sm text-gray-700">
              Are you sure you want to delete the purchase timer for store{' '}
              <strong>{deleteTarget.store}</strong>? This action cannot be
              undone.
            </p>
            <div className="mt-6 flex gap-4">
              <Button
                buttonText="Delete"
                className="w-full"
                type="button"
                loading={deleting}
                onClick={handleDelete}
              />
              <Button
                buttonText="Cancel"
                variant="outline"
                type="button"
                className="w-full"
                onClick={() => setDeleteTarget(null)}
              />
            </div>
          </>
        </Modal>
      )}
    </>
  )
}

export default PurchaseTimerSection
